/**
 * src/components/ReportFormModal.tsx
 * 日報入力フォーム（admin / assistant 共通）
 *
 * Props:
 *   mode         … 'admin' | 'assistant'
 *   assistants   … admin モード時に表示するアシスタント一覧
 *   myAssistantId … assistant モード時に使う自分の ID
 *   works        … 作品一覧（選択式）
 *   onClose      … 閉じるコールバック
 *   onSaved      … 保存完了コールバック
 */

import { useState } from 'react'
import { api } from '../api/client'
import { ConfirmDialog } from './ConfirmDialog'
import {
  extractClipThumbnail,
  ClipExtractionError,
} from '../utils/clipThumbnailExtractor'
import {
  STAGE_OPTIONS,
  KAIKAI_KIKI_ID,
  KAIKAI_KIKI_LABEL,
  isKaikaiKiki,
  emptyDetail,
  parseWorkDetails,
  legacyToDetail,
  parseImageUrls,
  normalizeImageUrl,
  type WorkDetail,
} from './reportUtils'
import { todayJst } from '../utils/jst'

type WorkOption = { id: string; title: string }
type AsstOption = { id: string; name: string }

type Props = {
  mode:           'admin' | 'assistant'
  assistants?:    AsstOption[]
  myAssistantId?: string
  works?:         WorkOption[]
  onClose:        () => void
  onSaved:        (id: string, name: string, isUpdate: boolean) => void
  /** 編集モード: 既存日報を渡すと初期値として展開する */
  initialReport?: Record<string, unknown>
}

export function ReportFormModal({
  mode, assistants = [], myAssistantId = '', works = [], onClose, onSaved,
  initialReport,
}: Props) {
  const today = todayJst()

  // 編集モード用: initialReport から初期値を組み立てる
  const isEditMode = !!initialReport
  const initDetails: WorkDetail[] = (() => {
    if (!initialReport) return [emptyDetail()]
    return parseWorkDetails(initialReport.work_details) ?? legacyToDetail(initialReport)
  })()
  const initImages: { url: string; key: string; name: string }[] = (() => {
    if (!initialReport) return []
    return parseImageUrls(initialReport.image_urls).map((url) => ({
      url:  normalizeImageUrl(url),
      key:  url,
      name: url.split('/').pop() ?? 'image',
    }))
  })()

  const [selectedAsstId, setSelectedAsstId] = useState(
    isEditMode
      ? String(initialReport!.assistant_id ?? (mode === 'assistant' ? myAssistantId : (assistants[0]?.id ?? '')))
      : (mode === 'assistant' ? myAssistantId : (assistants[0]?.id ?? ''))
  )
  const [reportDate,    setReportDate]    = useState(isEditMode ? String(initialReport!.report_date ?? today) : today)
  const [details,       setDetails]       = useState<WorkDetail[]>(initDetails)
  const [handoverNotes, setHandoverNotes] = useState(isEditMode ? String(initialReport!.handover_notes ?? '') : '')
  const [images,        setImages]        = useState<{ url: string; key: string; name: string }[]>(initImages)
  const [uploading,     setUploading]     = useState(false)
  const [clipConverting, setClipConverting] = useState(false)
  const [saving,        setSaving]        = useState(false)
  const [errors,        setErrors]        = useState<Record<string, string>>({})
  const [lightbox,      setLightbox]      = useState<string | null>(null)
  const [confirmOverwrite, setConfirmOverwrite] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [isDragging,    setIsDragging]    = useState(false)

  // 作業明細の操作
  const setDetail = (idx: number, key: keyof WorkDetail, val: string) =>
    setDetails((prev) => prev.map((d, i) => i === idx ? { ...d, [key]: val } : d))

  const setWorkForDetail = (idx: number, workId: string) => {
    if (workId === KAIKAI_KIKI_ID) {
      setDetails((prev) => prev.map((d, i) =>
        i === idx
          ? { ...d, work_id: KAIKAI_KIKI_ID, work_title: KAIKAI_KIKI_LABEL,
              episode_number: '', stage: '', pages_done: '' }
          : d
      ))
    } else {
      const w = works.find((w) => w.id === workId)
      setDetails((prev) => prev.map((d, i) =>
        i === idx
          ? { ...d, work_id: workId, work_title: w?.title ?? '' }
          : d
      ))
    }
  }

  const addDetail    = () => setDetails((prev) => [...prev, emptyDetail()])
  const removeDetail = (idx: number) => {
    if (details.length === 1) return
    setDetails((prev) => prev.filter((_, i) => i !== idx))
  }

  // ── 共通アップロード関数 ────────────────────────────────────────
  const uploadFiles = async (files: File[]) => {
    const clipFiles  = files.filter((f) => f.name.toLowerCase().endsWith('.clip'))
    const imageFiles = files.filter((f) => f.type.startsWith('image/'))

    // .clip ファイルをサムネイルに変換
    const extractedFiles: File[] = []
    if (clipFiles.length > 0) {
      setClipConverting(true)
      for (const clipFile of clipFiles) {
        try {
          const thumb = await extractClipThumbnail(clipFile)
          extractedFiles.push(thumb)
        } catch (err) {
          // ClipExtractionError (ClipSizeError を含む) のメッセージをそのまま表示
          const msg = err instanceof ClipExtractionError
            ? err.message
            : 'この.clipファイルからサムネイルを生成できませんでした。CLIP STUDIO PAINTからPNG/JPEGに書き出して添付してください。'
          alert(`「${clipFile.name}」\n${msg}`)
        }
      }
      setClipConverting(false)
    }

    const allImageFiles = [...imageFiles, ...extractedFiles]
    if (!allImageFiles.length) return

    setUploading(true)
    try {
      const results = await Promise.all(allImageFiles.map((f) => api.upload.reportImage(f)))
      setImages((prev) => [
        ...prev,
        ...results.map((r, i) => ({ url: r.url, key: r.key, name: allImageFiles[i].name })),
      ])
    } catch (err: any) { alert(`画像アップロード失敗: ${err.message}`) }
    finally { setUploading(false) }
  }

  // ── ファイル選択（既存） ────────────────────────────────────────
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadFiles(Array.from(e.target.files ?? []))
    e.target.value = ''
  }

  // ── Ctrl+V 貼り付け（モーダル全体で受け取る） ─────────────────
  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'))
    if (!files.length) return   // 画像以外（テキスト）は textarea 等に通常通り渡す
    e.preventDefault()
    uploadFiles(files)
  }

  // ── ドラッグ＆ドロップ ─────────────────────────────────────────
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    uploadFiles(Array.from(e.dataTransfer.files))
  }
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true) }
  const handleDragLeave = () => { setIsDragging(false) }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!reportDate) e.date = '日付を入力してください'
    if (mode === 'admin' && !selectedAsstId) e.asst = 'アシスタントを選択してください'
    if (!handoverNotes.trim()) e.handover = '引継ぎ・連絡事項を入力してください（「特になし」でもOK）'
    const hasWork = details.some((d) =>
      isKaikaiKiki(d.work_id, d.work_title)
        ? (d.memo ?? '').trim().length > 0   // カイカイキキは作業内容が必須
        : (d.work_title ?? '').trim().length > 0
    )
    if (!hasWork) e.details = '少なくとも1件の作業内容を入力してください'
    return e
  }

  const doSubmit = async () => {
    setSaving(true)
    try {
      const cleanDetails = details.filter((d) =>
        isKaikaiKiki(d.work_id, d.work_title)
          ? (d.memo ?? '').trim().length > 0
          : (d.work_title ?? '').trim().length > 0
      )
      const payload = {
        assistant_id:   selectedAsstId,
        report_date:    reportDate,
        work_details:   cleanDetails,
        handover_notes: handoverNotes,
        stage:          cleanDetails[0]?.stage ?? undefined,
        pages_done:     Number(cleanDetails[0]?.pages_done ?? 0),
        image_urls:     images.map((i) => i.url),
      } as any

      let result: any
      if (isEditMode && initialReport?.id) {
        // 編集モード: PUT /reports/:id
        result = await api.reports.update(String(initialReport.id), payload)
        result = { ...result, updated: true }
      } else {
        // 新規モード: POST /reports
        result = await api.reports.create(payload)
      }

      const asstName = mode === 'admin'
        ? (assistants.find((a) => a.id === selectedAsstId)?.name ?? '')
        : ''
      onSaved(result?.id ?? '', asstName, !!result?.updated)
    } catch (ex: any) {
      setErrors({ submit: ex.message ?? 'エラーが発生しました' })
    } finally { setSaving(false) }
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length > 0) { setErrors(e); return }
    // 編集モードは既存レコードを PUT で更新するため上書き確認不要
    if (!isEditMode) {
      try {
        // scope=target: 上書き判定は「提出対象アシスタント × 日付」のみを確認する。
        // assistant モードでは selectedAsstId は常に自分自身のIDだが、
        // サーバー側でも role=assistant はログイン中ユーザーのIDに強制するため二重に安全。
        // admin モードでは selectedAsstId（フォームで選択した代理提出対象）で絞り込む。
        // これにより「同じ日の他人の日報」を理由に誤って上書き確認が出ることを防ぐ。
        const existing = await api.reports.list({
          date:         reportDate,
          assistant_id: selectedAsstId,
          scope:        'target',
        })
        if (existing.length > 0) { setConfirmOverwrite(true); return }
      } catch { /* continue */ }
    }
    await doSubmit()
  }

  const hasUnsavedChanges = () => {
    const defaultAsstId = mode === 'assistant' ? myAssistantId : (assistants[0]?.id ?? '')
    if (selectedAsstId !== defaultAsstId) return true
    if (reportDate !== today) return true
    if (handoverNotes.trim().length > 0) return true
    if (images.length > 0) return true
    if (details.length !== 1) return true

    const d = details[0] ?? {}
    return ['work_id', 'work_title', 'episode_number', 'stage', 'pages_done', 'memo', 'work_data_path'].some((key) =>
      String((d as any)[key] ?? '').trim().length > 0
    )
  }

  const requestClose = () => {
    if (saving || uploading) return
    if (hasUnsavedChanges()) {
      setShowDiscardConfirm(true)
      return
    }
    onClose()
  }

  return (
    <div className="overlay">
      {confirmOverwrite && (
        <ConfirmDialog
          title="日報を上書きしますか？"
          message={`${reportDate} の日報がすでに存在します。\n上書きすると元の内容は復元できません。`}
          confirmLabel="上書き保存"
          onConfirm={() => { setConfirmOverwrite(false); doSubmit() }}
          onClose={() => setConfirmOverwrite(false)}
        />
      )}
      {showDiscardConfirm && (
        <ConfirmDialog
          title="入力内容を破棄しますか？"
          message="未送信の入力内容があります。閉じると入力内容は破棄されます。このまま閉じますか？"
          confirmLabel="破棄して閉じる"
          onConfirm={() => { setShowDiscardConfirm(false); onClose() }}
          onClose={() => setShowDiscardConfirm(false)}
        />
      )}
      {lightbox && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
        }} onClick={() => setLightbox(null)}>
          <img src={lightbox} style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 8 }} />
        </div>
      )}

      <div className="modal" style={{ maxWidth: 600 }} onPaste={handlePaste}>
        <div className="modal-header">
          <div className="modal-title">{isEditMode ? `日報を編集 — ${String(initialReport!.report_date ?? '')}` : '日報入力'}</div>
          <button className="btn btn-ghost btn-sm" onClick={requestClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* 日付 + アシスタント */}
          <div style={{ display: 'grid', gridTemplateColumns: mode === 'admin' ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 20 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>日付 <span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="date" value={reportDate}
                onChange={(e) => !isEditMode && setReportDate(e.target.value)}
                readOnly={isEditMode}
                style={{
                  borderColor: errors.date ? 'var(--red)' : undefined,
                  background: isEditMode ? 'var(--paper-2)' : undefined,
                  cursor: isEditMode ? 'default' : undefined,
                }} />
              {isEditMode && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                  日付は編集できません。日付を変更する場合は削除して再提出してください。
                </div>
              )}
              {errors.date && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.date}</div>}
            </div>
            {mode === 'admin' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>担当者 <span style={{ color: 'var(--red)' }}>*</span></label>
                <select value={selectedAsstId} onChange={(e) => setSelectedAsstId(e.target.value)}
                  style={{ borderColor: errors.asst ? 'var(--red)' : undefined }}>
                  {assistants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.asst && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.asst}</div>}
              </div>
            )}
          </div>

          {/* 作業明細 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
                  作業明細 <span style={{ color: 'var(--red)' }}>*</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  作品・工程・ページ数・作業内容を入力してください
                </div>
              </div>
            </div>
            {errors.details && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{errors.details}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {details.map((d, idx) => {
                const kk = isKaikaiKiki(d.work_id, d.work_title)
                return (
                  <div key={idx} style={{
                    background: kk ? 'var(--amber-light)' : 'var(--paper-2)',
                    borderRadius: 8, padding: '12px 14px',
                    border: `1px solid ${kk ? 'rgba(176,122,18,0.3)' : 'var(--paper-3)'}`,
                  }}>
                    {/* 行ヘッダー */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)' }}>作業 {idx + 1}</span>
                      {details.length > 1 && (
                        <button onClick={() => removeDetail(idx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 12, color: 'var(--red)', padding: '2px 6px' }}>
                          ✕ 削除
                        </button>
                      )}
                    </div>

                    {/* 作品選択 */}
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label>作品名</label>
                      {works.length > 0 ? (
                        <select
                          value={kk ? KAIKAI_KIKI_ID : (d.work_id ?? '')}
                          onChange={(e) => setWorkForDetail(idx, e.target.value)}
                        >
                          <option value="">— 選択してください —</option>
                          {works.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                          <option value={KAIKAI_KIKI_ID}>{KAIKAI_KIKI_LABEL}</option>
                        </select>
                      ) : (
                        <input type="text" value={d.work_title ?? ''}
                          onChange={(e) => setDetail(idx, 'work_title', e.target.value)}
                          placeholder="作品名を入力" />
                      )}
                    </div>

                    {/* 話数・工程・ページ（カイカイキキ時はdisabled） */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10,
                      opacity: kk ? 0.4 : 1,
                    }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>話数</label>
                        <input type="number" inputMode="numeric"
                          value={d.episode_number ?? ''} disabled={kk}
                          onChange={(e) => !kk && setDetail(idx, 'episode_number', e.target.value)}
                          placeholder="例: 6" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>工程</label>
                        <select value={d.stage ?? ''} disabled={kk}
                          onChange={(e) => !kk && setDetail(idx, 'stage', e.target.value)}>
                          <option value="">—</option>
                          {STAGE_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>完了ページ数</label>
                        <input type="number" inputMode="numeric"
                          value={d.pages_done ?? ''} disabled={kk}
                          onChange={(e) => !kk && setDetail(idx, 'pages_done', e.target.value)}
                          placeholder="例: 3" />
                      </div>
                    </div>
                    {kk && (
                      <div style={{ fontSize: 11, color: 'var(--amber)', marginBottom: 8 }}>
                        ⚠ カイカイキキ業務は話数・工程・ページ数の入力不要です
                      </div>
                    )}

                    {/* 作業内容（旧「作業メモ」→「作業内容」に統一） */}
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label>
                        作業内容
                        {kk && <span style={{ color: 'var(--red)', marginLeft: 4 }}>*</span>}
                      </label>
                      <textarea
                        value={d.memo ?? ''}
                        onChange={(e) => setDetail(idx, 'memo', e.target.value)}
                        placeholder={
                          kk
                            ? '例：\n・商品画像切り抜き 30点\n・チェック作業 2時間'
                            : '例：\n・教室背景、モブ配置まで完了\n・次回は廊下シーンから'
                        }
                        style={{ minHeight: kk ? 72 : 56 }}
                      />
                    </div>

                    {/* 作業データ保存先（作品・作業行ごと） */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <label style={{ marginBottom: 0 }}>作業データ保存先（任意）</label>
                        {idx === 0 && details.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const path = details[0]?.work_data_path ?? ''
                              setDetails((prev) => prev.map((row) => ({ ...row, work_data_path: path })))
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: 11, color: 'var(--blue)', padding: 0,
                            }}
                          >
                            全作業に同じ保存先を適用
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={d.work_data_path ?? ''}
                        onChange={(e) => setDetail(idx, 'work_data_path', e.target.value)}
                        placeholder="例：\\192.168.40.197\disk1\漫画\作品名\〇話"
                        maxLength={500}
                      />
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                        作業データを保存したフォルダの場所を入力してください。Windowsの場合は <code>\\192.168...</code>、
                        Macの場合は <code>smb://192.168...</code> のような表記で構いません。
                        保存先が分かりにくい場合は、作業フォルダを開いた状態のスクショも日報に添付してください。
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <button onClick={addDetail} className="btn btn-outline btn-sm" style={{ marginTop: 10 }}>
              ＋ 作業を追加
            </button>
          </div>

          {/* 引継ぎ・連絡事項 */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
              引継ぎ・連絡事項 <span style={{ color: 'var(--red)' }}>*</span>
            </label>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>
              翌日以降の作業者や管理者に伝えることを書いてください。特になければ「特になし」と入力してください。
            </div>
            <textarea
              value={handoverNotes}
              onChange={(e) => setHandoverNotes(e.target.value)}
              placeholder={`例：\n・〇〇ページの資料が不足しています\n・背景ラフは完了、仕上げ前に確認お願いします\n・明日は〇〇から着手予定です\n・特になし`}
              style={{
                minHeight: 80,
                borderColor: errors.handover ? 'var(--red)' : undefined,
                borderLeft: errors.handover ? undefined : '3px solid var(--amber)',
              }}
            />
            {errors.handover && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.handover}</div>}
          </div>

          {/* 画像アップロード */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
              作業画像（任意 / 複数可）
            </label>

            {/* ドロップゾーン */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              style={{
                border: `2px dashed ${isDragging ? 'var(--green)' : uploading ? 'var(--paper-3)' : 'var(--paper-3)'}`,
                borderRadius: 10,
                background: isDragging ? 'rgba(45,122,58,0.04)' : uploading ? 'var(--paper-2)' : 'var(--paper-2)',
                padding: '12px 14px',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {/* プレビュー + 追加ボタン */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: images.length > 0 ? 10 : 0 }}>
                {images.map((img) => (
                  <div key={img.key} style={{ position: 'relative' }}>
                    <img src={img.url} onClick={() => setLightbox(img.url)}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6,
                        cursor: 'pointer', border: '1px solid var(--paper-3)' }} />
                    <button onClick={() => setImages((prev) => prev.filter((i) => i.key !== img.key))}
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'var(--red)', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: 11,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>✕</button>
                  </div>
                ))}

                {/* ＋ボタン（ファイル選択） */}
                <label style={{
                  width: 80, height: 80, borderRadius: 6,
                  border: '2px dashed var(--paper-3)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  color: 'var(--ink-3)', fontSize: 11, gap: 4,
                  background: uploading ? 'var(--paper-3)' : 'var(--paper)',
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: 20 }}>{(uploading || clipConverting) ? '⏳' : '+'}</span>
                  <span>{clipConverting ? '生成中' : uploading ? 'UP中' : '追加'}</span>
                  <input type="file" accept="image/*,.clip" multiple onChange={handleImageSelect}
                    disabled={uploading || clipConverting} style={{ display: 'none' }} />
                </label>
              </div>

              {/* ヒントテキスト */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                fontSize: 11, color: isDragging ? 'var(--green)' : 'var(--ink-3)',
              }}>
                {clipConverting ? (
                  <span style={{ fontWeight: 600 }}>⏳ .clipファイルからサムネイルを生成しています…</span>
                ) : isDragging ? (
                  <span style={{ fontWeight: 600 }}>📂 ここでドロップして追加</span>
                ) : (
                  <>
                    <span>📎 クリック「＋」でファイル選択</span>
                    <span>⌨ Ctrl+V で貼り付け</span>
                    <span>🖼 ここへドラッグ＆ドロップ</span>
                  </>
                )}
              </div>
            </div>

            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 5 }}>
              JPEG / PNG / WebP・各5MBまで・サムネイルをクリックで拡大
              　／　.clip（CLIP STUDIO PAINT）はサムネイルを自動生成します（本体はアップロードされません）
            </div>
          </div>

          {errors.submit && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 12,
              padding: '8px 14px', background: '#fde8e6', borderRadius: 6 }}>
              ⚠ {errors.submit}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={requestClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || uploading || clipConverting}>
            {saving ? '保存中...' : clipConverting ? 'サムネイル生成中...' : uploading ? 'アップロード中...' : isEditMode ? '変更を保存' : '送信'}
          </button>
        </div>
      </div>
    </div>
  )
}